/**
 * What the platform said about the last thing a board recorded.
 *
 * The old boards save with a click and close their form at once — before the
 * platform could answer, and often before the person has looked back. So the
 * answer is said by the shell, wherever the person now is: "SR-2026-0007 filed
 * on the platform", or "Not saved on the platform: Not permitted".
 *
 * Kept in a module that imports nothing, for the reason `report.ts` gives: the
 * shell reads it on every screen, and anything heavier would pull the demo's
 * tables into the entry chunk.
 */

export type WriteTone = "ok" | "warn";

export interface WriteNoticeState {
  /** changes on every notice, so the same text twice still shows */
  seq: number;
  tone: WriteTone;
  text: string;
  /** offered when trying again cannot file the record twice */
  retry?: () => void;
}

let current: WriteNoticeState | null = null;
let seq = 0;
const listeners = new Set<() => void>();

const emit = () => {
  for (const fn of listeners) fn();
};

/** Say how the platform took a record. */
export function sayWrite(tone: WriteTone, text: string, retry?: () => void): void {
  seq += 1;
  current = { seq, tone, text, ...(retry ? { retry } : {}) };
  emit();
}

/** The person read it. */
export function clearWrite(): void {
  current = null;
  emit();
}

export const writeNotice = (): WriteNoticeState | null => current;

/** Shaped for `useSyncExternalStore`. */
export function subscribeWrite(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
